import { collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc, writeBatch, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Project, CollaboratorDocument, Invitation, CollaboratorRole } from '../types';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { sendEmailWithTemplate } from '../utils/emailService';
import  app  from '../config/firebase';
import { getAuth } from 'firebase/auth';

export class CollaborationService {
  static async inviteToProject(
    projectId: string,
    email: string,
    role: CollaboratorRole,
    inviterId: string
  ): Promise<void> {
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);

    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const project = projectDoc.data() as Project;
    
    if (!inviterId) {
      throw new Error('User must be logged in to invite collaborators');
    }

    // Create invitation with 96-hour expiration
    const expiresAt = Timestamp.fromDate(new Date(Date.now() + 96 * 60 * 60 * 1000));
    const token = uuidv4();
    const invitationsRef = collection(db, 'invitations');

    await addDoc(invitationsRef, {
      projectId,
      email: email.toLowerCase(),
      role,
      token,
      status: 'pending',
      invitedBy: inviterId,
      invitedAt: serverTimestamp(),
      expiresAt,
    });

    // Prepare the invite URL and send email
    const inviteUrl = `${window.location.origin}/invite/${token}`;
    const auth = getAuth(app);
    const currentUser = auth.currentUser;

    // Send the invitation email
    await sendEmailWithTemplate({
      to: email,
      templateId: 'd-5e901365ab8e4cecb255a9108f605229',
      dynamicData: {
        name: email.split('@')[0],
        projectTitle: project.title,
        invitedBy: currentUser?.email || '',
        inviteUrl,
        role,
        expiresAt: expiresAt.toDate().toLocaleDateString(),
      },
    });
  }

  static async removePendingInvitation(projectId: string, email: string): Promise<void> {
    const invitationsRef = collection(db, 'invitations');
    const pendingInvitationQuery = query(
      invitationsRef,
      where('projectId', '==', projectId),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );

    const pendingInvitations = await getDocs(pendingInvitationQuery);
    if (pendingInvitations.empty) {
      throw new Error('No pending invitation found');
    }

    // Delete all pending invitations for this email in this project
    const batch = writeBatch(db);
    pendingInvitations.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    await batch.commit();
  }

  static async resendInvitation(projectId: string, email: string): Promise<void> {
    const invitationsRef = collection(db, 'invitations');
    const invitationQuery = query(
      invitationsRef,
      where('projectId', '==', projectId),
      where('email', '==', email.toLowerCase()),
      where('status', '==', 'pending')
    );

    const invitationDocs = await getDocs(invitationQuery);
    if (invitationDocs.empty) {
      throw new Error('No pending invitation found');
    }

    const invitationDoc = invitationDocs.docs[0];
    const invitation = invitationDoc.data() as Invitation;

    // console.log("=== Resending Invitation ===");
    // console.log("Current invitation:", invitation);
    
    // Update expiration date and generate a new token for security
    const expiresAt = Timestamp.fromDate(new Date(Date.now() + 96 * 60 * 60 * 1000));
    const newToken = uuidv4();

    // console.log("New expiration date:", expiresAt.toDate());
    // console.log("New token:", newToken);

    // Update the invitation document
    await updateDoc(invitationDoc.ref, {
      expiresAt,
      token: newToken,
      status: 'pending',
      invitedAt: serverTimestamp(),
      previousToken: invitation.token // Keep track of previous token
    });

    // Get project details
    const projectRef = doc(db, 'projects', projectId);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }
    const project = projectDoc.data() as Project;

    // Prepare the new invite URL
    const inviteUrl = `${window.location.origin}/invite/${newToken}`;
    const auth = getAuth(app);
    const currentUser = auth.currentUser;

    // Send the invitation email
    await sendEmailWithTemplate({
      to: email,
      templateId: 'd-5e901365ab8e4cecb255a9108f605229',
      dynamicData: {
        name: email.split('@')[0],
        projectTitle: project.title,
        invitedBy: currentUser?.email || '',
        inviteUrl,
        role: invitation.role,
        expiresAt: expiresAt.toDate().toLocaleDateString(),
      },
    });

    // console.log("Invitation resent successfully with new token and expiration date");
  }

  static async acceptInvitation(
    tokenOrId: string,
    userId: string,
    userEmail: string
  ): Promise<{ projectId: string; projectTitle: string; requiresAuth: boolean; invitationEmail: string }> {
    const batch = writeBatch(db);
    const invitationsRef = collection(db, 'invitations');
    
    // Try to find invitation by token first, then by id if not found
    const tokenQuery = query(
      invitationsRef,
      where('token', '==', tokenOrId),
      where('status', '==', 'pending')
    );

    let invitationDocs = await getDocs(tokenQuery);
    
    // If not found by token, try by id
    if (invitationDocs.empty) {
      const idQuery = query(
        invitationsRef,
        where('__name__', '==', tokenOrId),
        where('status', '==', 'pending')
      );
      invitationDocs = await getDocs(idQuery);
    }

    if (invitationDocs.empty) {
      throw new Error('Invalid or expired invitation');
    }

    const invitationDoc = invitationDocs.docs[0];
    const invitation = {
      id: invitationDoc.id,
      ...invitationDoc.data()
    } as Invitation;

    // Convert Firestore Timestamp to JavaScript Date for comparison
    const expirationDate = (invitation.expiresAt as any).toDate();
    const currentDate = new Date();

    // console.log("=== Invitation Validation ===");
    // console.log("Token/ID:", tokenOrId);
    // console.log("Invitation Email:", invitation.email);
    // console.log("User Email:", userEmail);
    // console.log("Invitation Status:", invitation.status);
    // console.log("Expiration Date:", expirationDate);
    // console.log("Current Date:", currentDate);
    // console.log("Time Remaining (hours):", (expirationDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60));
    
    // Check if invitation has expired
    if (currentDate > expirationDate) {
      // console.log("Invitation expired - updating status");
      const now = Timestamp.now();
      await updateDoc(invitationDoc.ref, { 
        status: 'expired',
        expiredAt: now
      });
      throw new Error('This invitation has expired. Please request a new invitation.');
    }

    // First, check if a user is signed in
    if (!userId || !userEmail) {
      console.log("No user signed in - returning invitation details for authentication");
      return {
        projectId: invitation.projectId,
        projectTitle: '',  // We'll fetch this later when needed
        requiresAuth: true,
        invitationEmail: invitation.email
      };
    }

    // Check if the signed-in user's email matches the invitation recipient
    if (invitation.email.toLowerCase() !== userEmail.toLowerCase()) {
      console.log("Email mismatch - user needs to sign in with the correct account");
      return {
        projectId: invitation.projectId,
        projectTitle: '',
        requiresAuth: true,
        invitationEmail: invitation.email
      };
    }

    // At this point, we have a valid user who matches the invitation recipient
    const projectRef = doc(db, 'projects', invitation.projectId);
    // console.log("Looking up project:", {
    //   projectId: invitation.projectId,
    //   invitationId: invitation.id,
    //   userEmail: userEmail,
    //   userId: userId
    // });
    
    const projectDoc = await getDoc(projectRef);
    // console.log("Project document exists:", projectDoc.exists(), projectDoc.id);
    // console.log("Raw project data:", projectDoc.data());

    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const project = projectDoc.data() as Project;
    // console.log("Project data after cast:", {
    //   title: project.title,
    //   ownerId: project.ownerId,
    //   boards: project.boards?.length || 0
    // });

    // Check if user is already a collaborator
    const collaboratorsRef = collection(db, 'collaborators');
    const existingCollaboratorQuery = query(
      collaboratorsRef,
      where('projectId', '==', invitation.projectId),
      where('userId', '==', userId),
      where('status', '==', 'accepted')
    );

    const existingCollaborator = await getDocs(existingCollaboratorQuery);
    if (!existingCollaborator.empty) {
      // console.log("User is already a collaborator - updating invitation status");
      const now = Timestamp.now();
      batch.update(invitationDoc.ref, {
        status: 'accepted',
        acceptedAt: now,
      });
      await batch.commit();
      throw new Error('You are already a collaborator on this project');
    }

    const now = Timestamp.now();
    
    // Create new collaborator document
    const collaboratorRef = doc(collaboratorsRef);
    const newCollaborator: Omit<CollaboratorDocument, 'id'> = {
      projectId: invitation.projectId,
      userId: userId,
      email: userEmail.toLowerCase(),
      role: invitation.role,
      status: 'accepted',
      invitedAt: invitation.invitedAt instanceof Timestamp ? invitation.invitedAt : now,
      acceptedAt: now,
      invitedBy: invitation.invitedBy,
    };

    // console.log("Adding new collaborator:", newCollaborator);

    // Add the collaborator document
    batch.set(collaboratorRef, newCollaborator);

    // Update the current invitation status
    batch.update(invitationDoc.ref, {
      status: 'accepted',
      acceptedAt: now,
    });

    // Find and update any other pending invitations for this user in this project
    const otherInvitationsQuery = query(
      invitationsRef,
      where('projectId', '==', invitation.projectId),
      where('email', '==', userEmail.toLowerCase()),
      where('status', '==', 'pending')
    );

    const otherInvitations = await getDocs(otherInvitationsQuery);
    otherInvitations.docs.forEach(doc => {
      if (doc.id !== invitationDoc.id) {
        batch.update(doc.ref, {
          status: 'superseded',
          supersededAt: now,
        });
      }
    });

    // console.log("Committing batch with collaborator update");
    // Commit all changes atomically
    await batch.commit();
    // console.log("Batch committed successfully");

    // Return project details for redirection
    return {
      projectId: invitation.projectId,
      projectTitle: project.title,
      requiresAuth: false,
      invitationEmail: invitation.email
    };
  }

  static async rejectInvitation(tokenOrId: string): Promise<void> {
    const batch = writeBatch(db);
    const invitationsRef = collection(db, 'invitations');
    
    // Try to find invitation by token first, then by id if not found
    const tokenQuery = query(
      invitationsRef,
      where('token', '==', tokenOrId),
      where('status', '==', 'pending')
    );

    let invitationDocs = await getDocs(tokenQuery);
    
    // If not found by token, try by id
    if (invitationDocs.empty) {
      const idQuery = query(
        invitationsRef,
        where('__name__', '==', tokenOrId),
        where('status', '==', 'pending')
      );
      invitationDocs = await getDocs(idQuery);
    }

    if (invitationDocs.empty) {
      throw new Error('Invalid or expired invitation');
    }

    const invitationDoc = invitationDocs.docs[0];
    const invitation = invitationDoc.data() as Invitation;

    // Update the current invitation status
    batch.update(invitationDoc.ref, {
      status: 'rejected',
      rejectedAt: serverTimestamp(),
    });

    // Find and update any other pending invitations for this user in this project
    const otherInvitationsQuery = query(
      invitationsRef,
      where('projectId', '==', invitation.projectId),
      where('email', '==', invitation.email),
      where('status', '==', 'pending')
    );

    const otherInvitations = await getDocs(otherInvitationsQuery);
    otherInvitations.docs.forEach(doc => {
      if (doc.id !== invitationDoc.id) {
        batch.update(doc.ref, {
          status: 'superseded',
          supersededAt: serverTimestamp(),
        });
      }
    });

    await batch.commit();
  }

  static async updateCollaboratorRole(
    projectId: string,
    userId: string,
    newRole: CollaboratorRole
  ): Promise<void> {
    const collaboratorsRef = collection(db, 'collaborators');
    const collaboratorQuery = query(
      collaboratorsRef,
      where('projectId', '==', projectId),
      where('userId', '==', userId),
      where('status', '==', 'accepted')
    );

    const snapshot = await getDocs(collaboratorQuery);
    if (snapshot.empty) {
      throw new Error('Collaborator not found');
    }

    await updateDoc(snapshot.docs[0].ref, {
      role: newRole
    });
  }

  static async removeCollaborator(
    projectId: string,
    userId: string
  ): Promise<void> {
    const collaboratorsRef = collection(db, 'collaborators');
    const collaboratorQuery = query(
      collaboratorsRef,
      where('projectId', '==', projectId),
      where('userId', '==', userId),
      where('status', '==', 'accepted')
    );

    const snapshot = await getDocs(collaboratorQuery);
    if (snapshot.empty) {
      throw new Error('Collaborator not found');
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'removed',
        removedAt: serverTimestamp()
      });
    });

    await batch.commit();
  }

  static async getInvitation(token: string): Promise<Invitation> {
    const invitationsRef = collection(db, 'invitations');
    const q = query(invitationsRef, where('token', '==', token), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error('Invitation not found');
    }

    const invitationDoc = snapshot.docs[0];
    const invitation = { id: invitationDoc.id, ...invitationDoc.data() } as Invitation;
    
    // console.log("=== Getting Invitation ===");
    // console.log("Token:", token);
    // console.log("Invitation:", invitation);
    // console.log("Status:", invitation.status);
    
    if (invitation.status !== 'pending') {
      // console.log("Invitation status is not pending:", invitation.status);
      throw new Error(`Invitation has already been ${invitation.status}`);
    }

    const expirationDate = (invitation.expiresAt as any).toDate();
    const currentDate = new Date();
    
    // console.log("Expiration date:", expirationDate);
    // console.log("Current date:", currentDate);
    // console.log("Is expired:", currentDate > expirationDate);
    // console.log("Time difference (hours):", (expirationDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60));

    if (currentDate > expirationDate) {
      // console.log("Invitation has expired - updating status");
      await updateDoc(invitationDoc.ref, { 
        status: 'expired',
        expiredAt: serverTimestamp()
      });
      throw new Error('Invitation has expired');
    }

    return invitation;
  }

  static async getProjectCollaborators(
    projectId: string
  ): Promise<CollaboratorDocument[]> {
    const collaboratorsRef = collection(db, 'collaborators');
    const collaboratorQuery = query(
      collaboratorsRef,
      where('projectId', '==', projectId),
      where('status', '==', 'accepted')
    );

    const snapshot = await getDocs(collaboratorQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CollaboratorDocument));
  }
} 